import React, { useEffect, useState } from "react";
import { Box, Button, Collapse, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import FiltersBarCst from "@components/FiltersBarCst";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import { FiltersAccordionProps } from "./types";

const FiltersAccordionCst: React.FC<FiltersAccordionProps> = ({
  open,
  onClose,
  pageId,
  value,
  onApply,
  excludeKeys,
  filterVisibility,
  dynamicOptions,
  optionsKeyOverrides,
  title,
}) => {
  const { t } = useTranslation("catalog-v2");
  const theme = useTheme();

  const [draft, setDraft] = useState<FiltersBarValue>(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const handleReset = () => setDraft({});

  const handleApply = () => {
    onApply(draft);
  };

  return (
    <Collapse in={open} unmountOnExit>
      <Box
        sx={{
          mt: 2,
          padding: "20px 24px",
          backgroundColor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 2,
          }}
        >
          {title ?? t("filters-modal.title")}
        </Typography>

        <Box
          sx={{
            "& .MuiGrid-container": { marginTop: 0 },
            "& .MuiGrid-item": { paddingTop: "16px !important" },
          }}
        >
          <FiltersBarCst
            pageId={pageId}
            value={draft}
            onChange={setDraft}
            excludeKeys={excludeKeys}
            filterVisibility={filterVisibility}
            dynamicOptions={dynamicOptions}
            optionsKeyOverrides={optionsKeyOverrides}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mt: 2,
          }}
        >
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={handleReset}
            variant="text"
            sx={{
              textTransform: "none",
              color: theme.palette.primary.main,
              fontWeight: 600,
              textDecoration: "underline",
            }}
          >
            {t("filters-modal.reset")}
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "8px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            {t("filters-modal.apply")}
          </Button>
        </Box>
      </Box>
    </Collapse>
  );
};

export default FiltersAccordionCst;
